/**
 * Interval recognition — "What interval did you hear?"
 *
 * Plays the interval ascending in key context, after a cadence. The user picks
 * the interval name. Distractors are intervals 1-2 semitones away (the nearby
 * confusables) plus the interval's inversion (M3 ↔ m6 confusion is common).
 */

import { AudioPlanBuilder, type AudioPlan } from "../audio-plan.js";
import type { Exercise, AnswerOption } from "../exercise.js";
import { Rng } from "../exercise.js";
import { appendCadence, pickKey, type CadenceOptions } from "../utils.js";

const INTERVAL_SEMITONES: Record<string, number> = {
  "m2": 1, "M2": 2, "A2": 3, "m3": 3, "M3": 4, "P4": 5,
  "A4": 6, "d5": 6, "P5": 7, "m6": 8, "M6": 9, "d7": 9,
  "m7": 10, "M7": 11, "P8": 12,
};
const INTERVAL_NAMES: Record<string, string> = {
  "m2": "Minor 2nd", "M2": "Major 2nd", "A2": "Augmented 2nd",
  "m3": "Minor 3rd", "M3": "Major 3rd",
  "P4": "Perfect 4th", "A4": "Tritone (♯4)", "d5": "Tritone (♭5)",
  "P5": "Perfect 5th",
  "m6": "Minor 6th", "M6": "Major 6th", "d7": "Diminished 7th",
  "m7": "Minor 7th", "M7": "Major 7th",
  "P8": "Octave",
};

export interface IntervalParams {
  interval: string;
  context: "major" | "minor" | "chromatic";
  /** Melodic direction. Defaults to ascending. Harmonic is treated as ascending here. */
  direction?: "ascending" | "descending";
}

/** Return the inversion of an interval (semitone-wise). */
function invertSemitones(s: number): number {
  return 12 - s;
}

/** Pick distractors that are pedagogically useful. */
function pickDistractors(rng: Rng, correctSemis: number, n: number): string[] {
  const candidates = Object.entries(INTERVAL_SEMITONES)
    .filter(([_, semis]) => semis !== correctSemis)
    .map(([name, semis]) => {
      // Score by closeness — nearby intervals are more confusable.
      const dist = Math.abs(semis - correctSemis);
      const isInversion = invertSemitones(semis) === correctSemis;
      // Lower score = better distractor.
      const score = dist + (isInversion ? -0.5 : 0);
      return { name, score };
    })
    .sort((a, b) => a.score - b.score);
  // Take the top N closest, with a small chance of including a far one for variety.
  const close = candidates.slice(0, Math.min(n + 2, candidates.length));
  return rng.sample(close.map((c) => c.name), n);
}

export function generateInterval(
  itemId: string,
  params: IntervalParams,
  seed: number,
  cadenceOpts?: CadenceOptions,
): Exercise {
  const rng = new Rng(seed);
  const semitones = INTERVAL_SEMITONES[params.interval];
  if (semitones === undefined) throw new Error(`Unknown interval: ${params.interval}`);

  const key = pickKey(rng, params.context === "minor" ? "minor" : "major");

  // Build audio: cadence, then two ascending notes forming the interval.
  // Bottom note is randomized within the scale (or chromatically) to vary the exercise.
  // Constrain the range so the upper note stays comfortably below C7 (96).
  const maxLowMidi = 96 - semitones - 12; // leave headroom
  const lowDegree = rng.int(5) + 1;
  const candidateLow = key.tonicMidi + 12 + ((lowDegree - 1) * 2);
  const lowMidi = Math.min(candidateLow, maxLowMidi);
  const highMidi = lowMidi + semitones;

  const descending = params.direction === "descending";
  const firstMidi = descending ? highMidi : lowMidi;
  const secondMidi = descending ? lowMidi : highMidi;

  const builder = new AudioPlanBuilder();
  appendCadence(builder, key, cadenceOpts);
  builder.addNote(firstMidi, 0.9, 0.20, "target");
  builder.advance(0.95);
  builder.addNote(secondMidi, 1.3, 0.22, "target");
  builder.advance(1.4);
  const audio = builder.build();

  // Options: correct + 4 smart distractors, shuffled.
  const distractors = pickDistractors(rng, semitones, 4);
  const allNames = rng.shuffle([params.interval, ...distractors]);
  const options: AnswerOption[] = allNames.map((name) => ({
    id: name,
    label: INTERVAL_NAMES[name] ?? name,
    sublabel: name,
  }));

  return {
    itemId,
    exerciseType: "interval-recognition",
    audio,
    prompt: "What interval did you hear?",
    options,
    correctAnswerId: params.interval,
    meta: {
      keyName: key.name,
      seed,
      explanation: `${INTERVAL_NAMES[params.interval]} (${params.interval}, ${semitones} semitones).`,
      distractorCount: distractors.length,
      teachAudio: buildIntervalTeachPlan(firstMidi, secondMidi),
    },
  };
}

/**
 * Wrong-answer teach plan for intervals: hear the two notes in isolation,
 * each held longer than during the original presentation, then both
 * sounded together so the user can lock in the interval's "color."
 *
 * The original audio is "cadence + low + high"; this strips the cadence
 * (which serves orientation, not interval recognition) and gives more
 * time on each pitch.
 */
export function buildIntervalTeachPlan(firstMidi: number, secondMidi: number): AudioPlan {
  const builder = new AudioPlanBuilder();
  builder.addNote(firstMidi, 1.1, 0.22, "melody");
  builder.advance(1.4);
  builder.addNote(secondMidi, 1.1, 0.22, "melody");
  builder.advance(1.4);
  builder.addChord([firstMidi, secondMidi], 1.6, 0.15, "harmony");
  builder.advance(1.7);
  return builder.build();
}
